import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  InputAdornment,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SaveIcon from "@mui/icons-material/Save";
import PrintIcon from "@mui/icons-material/Print";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../../api/useFetchObjects";
import useAdd from "../../../../api/useAdd";
import useUpdate from "../../../../api/useUpdate";
import useDelete from "../../../../api/useDelete";
import { useAuth } from "../../../../context";
import Print from "../TestsTobeDone/Print";

const LabTestByTicket = () => {
  const { t } = useTranslation();
  const token = useAuth()?.user?.token;
  const printRef = useRef();

  const [ticketSearch, setTicketSearch] = useState("");
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [results, setResults] = useState({});
  const [formErrors, setFormErrors] = useState({});

  const apiUrl =
    ticketSearch.trim() === ""
      ? ""
      : `laboratory-test-orders/?search=${encodeURIComponent(ticketSearch.trim())}`;

  const { data: ordersData, isLoading: loadingOrders } = useFetchObjects(
    ["laboratory-test-orders-by-ticket", ticketSearch.trim(), searchTrigger],
    apiUrl,
    token,
    { enabled: ticketSearch.trim() !== "" }
  );

  const orders = ordersData?.results ?? (Array.isArray(ordersData) ? ordersData : []);

  const { handleAdd, loading: addLoading } = useAdd("laboratory-results", token);
  const { handleUpdate, loading: updateLoading } = useUpdate(
    "laboratory-results",
    token
  );
  const { handleDelete, ConfirmDialog } = useDelete(
    "laboratory-patient-tests",
    token
  );

  useEffect(() => {
    if (selectedOrder?.tests_display) {
      const initialResults = {};
      selectedOrder.tests_display.forEach((test) => {
        const resultType =
          test.test_type_display?.result_type || "Numeric";
        initialResults[test.id] = {
          value: test.test_type_display?.result_value || "",
          resultId: test.test_type_display?.result_id ?? null,
          resultType,
        };
      });
      setResults(initialResults);
      setFormErrors({});
    }
  }, [selectedOrder]);

  const handleSearch = () => {
    setSelectedOrder(null);
    setSearchTrigger((prev) => prev + 1);
  };

  const handleResultChange = (testId, value) => {
    setResults((prev) => ({
      ...prev,
      [testId]: { ...prev[testId], value },
    }));
    setFormErrors((prev) => ({ ...prev, [testId]: "" }));
  };

  const handleBinaryChange = (testId, value) => {
    handleResultChange(testId, value);
  };

  const handleSaveResults = async () => {
    if (!selectedOrder?.tests_display) return;

    const childResults = Object.entries(results).filter(([testId]) => {
      const test = selectedOrder.tests_display.find(
        (t) => t.id === parseInt(testId)
      );
      return test?.parent_test !== null;
    });

    const standaloneResults = Object.entries(results).filter(([testId]) => {
      const test = selectedOrder.tests_display.find(
        (t) => t.id === parseInt(testId)
      );
      return (
        test?.parent_test === null && test?.child_tests === null
      );
    });

    const missingResults = [...childResults, ...standaloneResults].filter(
      ([_, { value, resultType }]) => {
        if (resultType === "Numeric") return !value;
        if (resultType === "Binary") return value === "";
        return false;
      }
    );

    if (missingResults.length > 0) {
      setFormErrors(
        missingResults.reduce(
          (acc, [testId]) => ({
            ...acc,
            [testId]: t("laboratory.resultRequired"),
          }),
          {}
        )
      );
      return;
    }

    try {
      const updatePromises = [
        ...childResults.map(
          async ([testId, { value, resultId, resultType }]) => {
            const resultData = {
              test: testId,
              result_value: value,
              result_type: resultType,
            };
            if (resultId) {
              await handleUpdate(resultId, resultData);
            } else {
              await handleAdd({ results: [resultData] });
            }
          }
        ),
        ...standaloneResults.map(
          async ([testId, { value, resultId, resultType }]) => {
            const resultData = {
              test: testId,
              result_value: value,
              result_type: resultType,
            };
            if (resultId) {
              await handleUpdate(resultId, resultData);
            } else {
              await handleAdd({ results: [resultData] });
            }
          }
        ),
      ];
      await Promise.all(updatePromises);
      setSearchTrigger((prev) => prev + 1);
      // Refetch this order so print shows updated results
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/laboratory-test-orders/${selectedOrder.id}/`,
        { headers: token ? { Authorization: `Token ${token}` } : {} }
      );
      if (res.ok) {
        const updated = await res.json();
        setSelectedOrder(updated);
      }
    } catch (error) {
      console.error("Error saving results:", error);
    }
  };

  const handlePrint = () => {
    if (!selectedOrder || !printRef.current) return;
    setTimeout(() => {
      const printWindow = window.open("", "_blank");
      printWindow.document.write(printRef.current.innerHTML);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const childTests =
    selectedOrder?.tests_display?.filter(
      (test) => test.parent_test !== null
    ) || [];
  const standaloneTests =
    selectedOrder?.tests_display?.filter(
      (test) =>
        test.parent_test === null && test.child_tests === null
    ) || [];

  const groupedTests = childTests.reduce((acc, test) => {
    const parentId = test.parent_test?.id;
    if (!parentId) return acc;
    if (!acc[parentId]) {
      acc[parentId] = { parent: test.parent_test.name, tests: [] };
    }
    acc[parentId].tests.push(test);
    return acc;
  }, {});

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Typography variant="h5" fontWeight="bold" color="primary" gutterBottom>
        {t("laboratory.testByTicket")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("laboratory.testByTicketDescription")}
      </Typography>

      {/* Ticket search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
          <TextField
            fullWidth
            size="small"
            label={t("laboratory.ticketNumber")}
            placeholder={t("laboratory.ticketNumberPlaceholder")}
            value={ticketSearch}
            onChange={(e) => setTicketSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            startIcon={<SearchIcon />}
            disabled={!ticketSearch.trim()}
          >
            {t("laboratory.search")}
          </Button>
        </Stack>
      </Paper>

      {loadingOrders && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {!loadingOrders && ticketSearch.trim() && searchTrigger > 0 && orders.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("laboratory.noTestsForTicket")}
        </Alert>
      )}

      {!loadingOrders && orders.length > 1 && !selectedOrder && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {t("laboratory.selectOrderForTicket")}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "primary.main" }}>
                  <TableCell sx={{ color: "white" }}>
                    {t("laboratory.ticketNumber")}
                  </TableCell>
                  <TableCell sx={{ color: "white" }}>
                    {t("laboratory.patientName")}
                  </TableCell>
                  <TableCell sx={{ color: "white" }}>
                    {t("laboratory.orderDate")}
                  </TableCell>
                  <TableCell sx={{ color: "white" }}>
                    {t("laboratory.resultStatus")}
                  </TableCell>
                  <TableCell sx={{ color: "white" }}>{t("laboratory.action")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell>{order.ticket_display || "-"}</TableCell>
                    <TableCell>{order.patient_display?.name || "-"}</TableCell>
                    <TableCell>
                      {order.order_date
                        ? new Date(order.order_date).toLocaleDateString()
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {order.has_results ? (
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{ color: "success.main", fontWeight: 500 }}
                        >
                          {t("laboratory.resultGiven")}
                        </Typography>
                      ) : (
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.secondary"
                        >
                          {t("laboratory.pendingResult")}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant={order.has_results ? "contained" : "outlined"}
                        onClick={() => setSelectedOrder(order)}
                      >
                        {order.has_results
                          ? t("laboratory.viewOrEditResult")
                          : t("laboratory.addResult")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {!loadingOrders && orders.length === 1 && !selectedOrder && (
        <Button
          variant="contained"
          onClick={() => setSelectedOrder(orders[0])}
          sx={{ mb: 2 }}
        >
          {t("laboratory.viewAndEnterResults")}
        </Button>
      )}

      {selectedOrder && (
        <>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setSelectedOrder(null)}
            sx={{ mb: 2 }}
          >
            {t("laboratory.backToOrderList")}
          </Button>

          {/* Patient info */}
          <Box
            sx={{
              p: 3,
              bgcolor: "white",
              borderRadius: 2,
              boxShadow: 2,
              mb: 3,
              borderLeft: "5px solid #007174",
            }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1">
                  <strong>{t("laboratory.patientName")}:</strong>{" "}
                  {selectedOrder?.patient_display?.name ?? t("laboratory.noData")}
                </Typography>
                <Typography variant="subtitle1">
                  <strong>{t("laboratory.ticketNumber")}:</strong>{" "}
                  {selectedOrder?.ticket_display ?? "-"}
                </Typography>
                <Typography variant="subtitle1">
                  <strong>{t("laboratory.patientId")}:</strong>{" "}
                  {selectedOrder?.patient_display?.id ?? t("laboratory.noData")}
                </Typography>
                <Typography variant="subtitle1">
                  <strong>{t("laboratory.orderDate")}:</strong>{" "}
                  {selectedOrder?.order_date
                    ? new Date(selectedOrder.order_date).toLocaleDateString()
                    : t("laboratory.noData")}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1">
                  <strong>{t("laboratory.doctorName")}:</strong>{" "}
                  {selectedOrder?.doctor_display?.username ?? t("laboratory.noData")}
                </Typography>
                <Typography variant="subtitle1">
                  <strong>{t("laboratory.urgency")}:</strong>{" "}
                  {selectedOrder?.urgency
                    ? t("laboratory.urgent")
                    : t("laboratory.routine")}
                </Typography>
                <Typography variant="subtitle1">
                  <strong>{t("laboratory.totalAmount")}:</strong>{" "}
                  {selectedOrder?.total_amount ?? "0.00"}؋
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Grouped child tests */}
          {Object.keys(groupedTests).length > 0 &&
            Object.entries(groupedTests).map(([parentId, group]) => (
              <Box key={parentId} sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                  {group.parent}
                </Typography>
                <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#007174" }}>
                        <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>#</TableCell>
                        <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                          {t("laboratory.testName")}
                        </TableCell>
                        <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                          {t("laboratory.resultValue")}
                        </TableCell>
                        <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                          {t("laboratory.normalRange")} ({t("laboratory.units")})
                        </TableCell>
                        <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                          {t("laboratory.action")}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody sx={{ bgcolor: "#ccfcfc" }}>
                      {group.tests.map((test, index) => (
                        <TableRow key={test.id}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{test.test_type_display?.name}</TableCell>
                          <TableCell>
                            {test.test_type_display?.result_type === "Binary" ? (
                              <>
                                <TextField
                                  select
                                  size="small"
                                  value={
                                    results[test.id]?.value === "Positive" ||
                                    results[test.id]?.value === "Negative"
                                      ? results[test.id]?.value
                                      : ""
                                  }
                                  onChange={(e) =>
                                    handleBinaryChange(test.id, e.target.value)
                                  }
                                  sx={{ width: 100, mr: 1 }}
                                >
                                  <MenuItem value="Positive">Positive</MenuItem>
                                  <MenuItem value="Negative">Negative</MenuItem>
                                </TextField>
                                <TextField
                                  size="small"
                                  placeholder="Custom"
                                  value={
                                    results[test.id]?.value === "Positive" ||
                                    results[test.id]?.value === "Negative"
                                      ? ""
                                      : results[test.id]?.value ?? ""
                                  }
                                  onChange={(e) =>
                                    handleResultChange(test.id, e.target.value)
                                  }
                                  sx={{ width: 200 }}
                                />
                              </>
                            ) : (
                              <TextField
                                size="small"
                                placeholder={t("laboratory.resultValue")}
                                value={results[test.id]?.value ?? ""}
                                onChange={(e) =>
                                  handleResultChange(test.id, e.target.value)
                                }
                                sx={{ width: 200 }}
                                error={!!formErrors[test.id]}
                                helperText={formErrors[test.id]}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {test.test_type_display?.reference_range ??
                              (test.test_type_display?.min_value != null &&
                              test.test_type_display?.max_value != null
                                ? `${test.test_type_display.min_value} - ${test.test_type_display.max_value} ${test.test_type_display?.units ?? ""}`
                                : "-")}
                          </TableCell>
                          <TableCell>
                            <Tooltip title={t("laboratory.deleteTest")}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(test.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))}

          {/* Standalone tests */}
          {standaloneTests.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                {t("laboratory.test")}
              </Typography>
              <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#007174" }}>
                      <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>#</TableCell>
                      <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                        {t("laboratory.testName")}
                      </TableCell>
                      <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                        {t("laboratory.resultValue")}
                      </TableCell>
                      <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                        {t("laboratory.normalRange")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ bgcolor: "#ccfcfc" }}>
                    {standaloneTests.map((test, index) => (
                      <TableRow key={test.id}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{test.test_type_display?.name}</TableCell>
                        <TableCell>
                          {test.test_type_display?.result_type === "Binary" && (
                            <>
                              <TextField
                                select
                                size="small"
                                value={
                                  results[test.id]?.value === "Positive" ||
                                  results[test.id]?.value === "Negative"
                                    ? results[test.id]?.value
                                    : ""
                                }
                                onChange={(e) =>
                                  handleBinaryChange(test.id, e.target.value)
                                }
                                sx={{ width: 100, mr: 1 }}
                              >
                                <MenuItem value="Positive">Positive</MenuItem>
                                <MenuItem value="Negative">Negative</MenuItem>
                              </TextField>
                              <TextField
                                size="small"
                                placeholder="Custom"
                                value={
                                  results[test.id]?.value === "Positive" ||
                                  results[test.id]?.value === "Negative"
                                    ? ""
                                    : results[test.id]?.value ?? ""
                                }
                                onChange={(e) =>
                                  handleResultChange(test.id, e.target.value)
                                }
                                sx={{ width: 200 }}
                              />
                            </>
                          )}
                          {test.test_type_display?.result_type === "Numeric" && (
                            <TextField
                              size="small"
                              placeholder={t("laboratory.resultValue")}
                              value={results[test.id]?.value ?? ""}
                              onChange={(e) =>
                                handleResultChange(test.id, e.target.value)
                              }
                              sx={{ width: 200 }}
                              error={!!formErrors[test.id]}
                              helperText={formErrors[test.id]}
                            />
                          )}
                          {test.test_type_display?.result_type === "Other" && (
                            <TextField
                              size="small"
                              placeholder={t("laboratory.resultValue")}
                              value={results[test.id]?.value ?? ""}
                              onChange={(e) =>
                                handleResultChange(test.id, e.target.value)
                              }
                              sx={{ width: 200 }}
                              error={!!formErrors[test.id]}
                              helperText={formErrors[test.id]}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {test.test_type_display?.min_value} -{" "}
                          {test.test_type_display?.max_value}{" "}
                          {test.test_type_display?.units ?? ""}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          <ConfirmDialog />

          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveResults}
              disabled={addLoading || updateLoading}
            >
              {addLoading || updateLoading
                ? t("laboratory.saving")
                : t("laboratory.saveResults")}
            </Button>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
            >
              {t("laboratory.print")}
            </Button>
          </Stack>

          {/* Hidden print content */}
          <Box ref={printRef} sx={{ position: "absolute", left: -9999, top: 0 }}>
            <Print
              labTest={selectedOrder}
              tests={selectedOrder?.tests_display ?? []}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default LabTestByTicket;
